import React, { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Check, Loader2, ArrowRight, ShoppingCart, BookOpen } from "lucide-react";
import { toast } from "sonner";
import { TID } from "@/constants/testIds";
import PaymentBadges from "@/components/landing/PaymentBadges";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AMAZON_URL = "https://amzn.eu/d/0iLs59IO";

const packages = [
  {
    id: "book_standard",
    name: "Paperback",
    price: "19,90",
    currency: "€",
    tagline: "Edizione cartacea",
    icon: BookOpen,
    features: [
      "188 pagine · 14,8 × 21 cm",
      "Tutti i capitoli del metodo",
      "Accesso a consulenza gratuita di orientamento",
      "Spedizione Amazon Prime disponibile",
    ],
    highlight: false,
    amazon: true,
  },
  {
    id: "bundle_call",
    name: "Bundle Pro",
    price: "49,00",
    currency: "€",
    tagline: "Libro + Call 1:1",
    icon: BookOpen,
    features: [
      "Edizione Paperback del libro",
      "Call privata di orientamento di 30 min con Michele",
      "Diagnosi personalizzata + piano d'azione",
      "Accesso community privata WhatsApp",
      "Risparmi €30 vs servizi singoli",
    ],
    highlight: true,
    amazon: false,
    bundle: true,
  },
  {
    id: "book_deluxe",
    name: "Kindle",
    price: "7,90",
    currency: "€",
    tagline: "Edizione digitale",
    icon: BookOpen,
    features: [
      "Disponibile istantaneamente",
      "Sincronizzazione su tutti i tuoi device",
      "Evidenziazioni e note integrate",
      "Lettore Kindle gratuito (no device richiesto)",
    ],
    highlight: false,
    amazon: true,
  },
];

export default function Pricing() {
  const [loadingId, setLoadingId] = useState(null);

  const handleStripe = async (pkgId) => {
    setLoadingId(pkgId);
    try {
      const res = await axios.post(`${API}/checkout/session`, {
        package_id: pkgId,
        origin_url: window.location.origin,
      });
      if (res.data?.url) {
        window.location.href = res.data.url;
      } else {
        toast.error("Errore nella creazione della sessione di pagamento.");
      }
    } catch (err) {
      toast.error("Impossibile avviare il pagamento. Riprova.");
      console.error(err);
    } finally {
      setLoadingId(null);
    }
  };

  return (
    <section
      id="pricing"
      data-testid={TID.pricingSection}
      className="py-24 md:py-32 border-t border-[#E5E1DA] relative"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16">
        <div className="text-center max-w-2xl mx-auto mb-16">
          <p className="overline">Acquista</p>
          <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl mt-4 leading-tight">
            Scegli il tuo
            <br />
            <em className="text-[#E89B6C]">formato.</em>
          </h2>
          <p className="mt-6 text-lg font-light text-[#3A4F6B]">
            Disponibile su Amazon Italia in formato cartaceo e Kindle.
            Pagamento sicuro e spedizione rapida.
          </p>
          <a
            href="#carta-docente"
            data-testid="pricing-carta-docente-badge"
            className="inline-flex items-center gap-2 mt-6 px-4 py-2 border border-[#0E7C8F]/40 bg-[#D6E5EC]/40 text-[11px] uppercase tracking-[0.16em] text-[#0B192C] hover:bg-[#0E7C8F] hover:text-[#F9F8F6] transition-colors"
          >
            ✓ Accetto Carta del Docente · 18app
          </a>
        </div>

        <div className="grid md:grid-cols-3 gap-px bg-[#E5E1DA] border border-[#0B192C] max-w-6xl mx-auto">
          {packages.map((pkg, i) => {
            const Icon = pkg.icon;
            return (
              <motion.div
                key={pkg.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7, delay: i * 0.1 }}
                className={`relative p-10 md:p-14 ${
                  pkg.highlight
                    ? "bg-[#0B192C] text-[#F9F8F6]"
                    : "bg-[#FFFFFF] text-[#0B192C]"
                }`}
                data-testid={`pricing-card-${pkg.id}`}
              >
                {pkg.highlight && (
                  <span className="absolute top-6 right-6 overline text-[#D4B895]">
                    Più scelto
                  </span>
                )}
                <Icon
                  size={28}
                  strokeWidth={1}
                  className={pkg.highlight ? "text-[#D4B895]" : "text-[#D4B895]"}
                />
                <p className="overline mt-6 text-[#D4B895]">{pkg.tagline}</p>
                <h3 className="font-serif-display text-3xl md:text-4xl mt-2">
                  {pkg.name}
                </h3>

                <div className="mt-6 flex items-baseline gap-2">
                  <span className="font-serif-display text-6xl md:text-7xl">
                    {pkg.price}
                  </span>
                  <span className="font-serif-display text-2xl">{pkg.currency}</span>
                </div>
                <p
                  className={`mt-2 text-xs uppercase tracking-[0.2em] ${
                    pkg.highlight ? "text-[#F9F8F6]/70" : "text-[#3A4F6B]"
                  }`}
                >
                  Venduto e spedito da Amazon
                </p>

                <ul className="mt-10 space-y-4">
                  {pkg.features.map((f) => (
                    <li key={f} className="flex items-start gap-3">
                      <Check
                        size={16}
                        strokeWidth={1.5}
                        className={`mt-1 flex-shrink-0 ${
                          pkg.highlight ? "text-[#D4B895]" : "text-[#0B192C]"
                        }`}
                      />
                      <span
                        className={`text-base font-light leading-relaxed ${
                          pkg.highlight ? "text-[#F9F8F6]" : "text-[#3A4F6B]"
                        }`}
                      >
                        {f}
                      </span>
                    </li>
                  ))}
                </ul>

                <div className="mt-12 space-y-3">
                  {pkg.bundle ? (
                    <a
                      href="https://wa.me/393773660476?text=Ciao%20Michele%2C%20vorrei%20acquistare%20il%20Bundle%20Pro%20(Libro%20%2B%20Call%2030%20min)%20a%20%E2%82%AC49."
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`${TID.pricingExternalBtn}-${pkg.id}`}
                      className="group w-full inline-flex items-center justify-center gap-3 px-8 py-4 bg-[#E89B6C] text-[#0B192C] text-sm uppercase tracking-[0.2em] hover:bg-[#F9F8F6] transition-all"
                    >
                      <ShoppingCart size={16} />
                      Richiedi il Bundle
                      <ArrowRight
                        size={16}
                        className="transition-transform group-hover:translate-x-1"
                      />
                    </a>
                  ) : (
                    <a
                      href={AMAZON_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`${TID.pricingExternalBtn}-${pkg.id}`}
                      className={`group w-full inline-flex items-center justify-center gap-3 px-8 py-4 text-sm uppercase tracking-[0.2em] transition-all ${
                        pkg.highlight
                          ? "bg-[#D4B895] text-[#0B192C] hover:bg-[#F9F8F6]"
                          : "bg-[#0B192C] text-[#F9F8F6] hover:bg-[#1E3557]"
                      }`}
                    >
                      <ShoppingCart size={16} />
                      Acquista su Amazon
                      <ArrowRight
                        size={16}
                        className="transition-transform group-hover:translate-x-1"
                      />
                    </a>
                  )}
                  {pkg.id === "book_standard" && (
                    <>
                      <button
                        data-testid={`${TID.pricingStripeBtn}-${pkg.id}`}
                        disabled={loadingId === pkg.id}
                        onClick={() => handleStripe(pkg.id)}
                        className={`w-full inline-flex items-center justify-center gap-2 px-8 py-4 text-xs uppercase tracking-[0.2em] border transition-colors ${
                          pkg.highlight
                            ? "border-[#F9F8F6]/30 text-[#F9F8F6] hover:border-[#F9F8F6]"
                            : "border-[#0B192C]/30 text-[#0B192C] hover:border-[#0B192C]"
                        } disabled:opacity-60`}
                      >
                        {loadingId === pkg.id ? (
                          <>
                            <Loader2 size={14} className="animate-spin" />
                            Apertura...
                          </>
                        ) : (
                          "Paga con carta · Apple Pay · Google Pay"
                        )}
                      </button>
                      <PaymentBadges
                        className="justify-center pt-2"
                        variant={pkg.highlight ? "dark" : "light"}
                      />
                    </>
                  )}
                </div>
              </motion.div>
            );
          })}
        </div>

        <p className="text-center mt-10 text-xs uppercase tracking-[0.2em] text-[#3A4F6B]">
          ISBN 979-1282209199 · Reso entro 14 giorni · Carta del Docente accettata
        </p>
        <p className="text-center mt-3 text-[10px] uppercase tracking-[0.18em] text-[#3A4F6B]/70">
          Pagamenti sicuri tramite Stripe · I bottoni Apple Pay / Google Pay
          appaiono automaticamente sul tuo dispositivo se supportati
        </p>
      </div>
    </section>
  );
}
